import { getSect } from '@jianghu/game-data';
import type { CharacterSummaryDTO } from '@jianghu/protocol';
import { useEffect, useState } from 'react';
import { CharacterPreview } from '../components/CharacterPreview';
import { Logo } from '../components/Logo';
import { audio } from '../game/audio/audio';
import { api, ApiError } from '../services/api';
import { useSession } from '../stores/session-store';

export function CharacterSelectScreen() {
  const realm = useSession((s) => s.realm);
  const [chars, setChars] = useState<CharacterSummaryDTO[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<CharacterSummaryDTO | null>(null);
  const [confirmName, setConfirmName] = useState('');

  const load = (): void => {
    if (!realm) return;
    api
      .characters(realm.id)
      .then((c) => {
        setChars(c);
        const last = [...c].sort((a, b) => (b.lastLoginAt ?? '').localeCompare(a.lastLoginAt ?? ''))[0];
        setSelected((cur) => (cur && c.some((x) => x.id === cur) ? cur : (last?.id ?? null)));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load characters'));
  };
  useEffect(load, [realm]);

  if (!realm) {
    useSession.getState().go('realms');
    return null;
  }
  const current = chars?.find((c) => c.id === selected) ?? null;

  const enter = (): void => {
    if (!current) return;
    audio.play('ui_confirm');
    useSession.getState().setCharacter(current);
    useSession.getState().go('game');
  };

  const doDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await api.deleteCharacter(deleting.id, confirmName);
      setDeleting(null);
      setConfirmName('');
      setSelected(null);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Delete failed');
    }
  };

  return (
    <main className="screen">
      <div className="col" style={{ alignItems: 'center', width: '100%' }}>
        <Logo compact />
        <div className="select-layout">
          <section className="panel col" aria-labelledby="chars-title">
            <h2 className="panel-title" id="chars-title">
              {realm.name}{' '}
              <span className="muted" style={{ fontSize: 14 }}>
                {realm.nameZh}
              </span>
            </h2>
            {error && <div className="form-error">{error}</div>}
            {!chars && <div className="spinner" style={{ margin: '20px auto' }} />}
            {chars && chars.length === 0 && (
              <p className="muted">No martial artists yet. Create your first character.</p>
            )}
            <div className="char-list" role="listbox" aria-label="Characters">
              {chars?.map((c) => (
                <button
                  key={c.id}
                  className="char-item"
                  role="option"
                  aria-selected={selected === c.id}
                  aria-pressed={selected === c.id}
                  onClick={() => setSelected(c.id)}
                  onDoubleClick={enter}
                >
                  <span style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 17 }}>{c.name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      Level {c.level} · {getSect(c.sect)?.name ?? c.sect}
                    </div>
                  </span>
                </button>
              ))}
            </div>
            <span className="spacer" />
            <button className="btn btn-primary btn-block" disabled={!current} onClick={enter}>
              Enter World
            </button>
            <div className="row">
              <button
                className="btn"
                onClick={() => useSession.getState().go('create')}
                disabled={(chars?.length ?? 0) >= 4}
              >
                Create Character
              </button>
              <span className="spacer" />
              <button
                className="btn btn-danger btn-sm"
                disabled={!current}
                onClick={() => current && setDeleting(current)}
              >
                Delete
              </button>
            </div>
            <button className="btn btn-ghost" onClick={() => useSession.getState().go('realms')}>
              Change realm
            </button>
          </section>
          {current ? (
            <CharacterPreview
              sect={current.sect}
              appearance={current.appearance}
              weapon={current.weapon}
              armor={current.armor}
              caption={current.name}
              subcaption={`Level ${current.level} · ${getSect(current.sect)?.name ?? ''}`}
            />
          ) : (
            <div className="preview" />
          )}
        </div>
      </div>
      {deleting && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="del-title">
          <div className="panel modal">
            <h2 className="panel-title" id="del-title">
              Delete {deleting.name}?
            </h2>
            <p className="muted">
              This retires the character from play. Type <strong className="gold">{deleting.name}</strong> to
              confirm.
            </p>
            <input
              className="input"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              autoFocus
              aria-label="Confirm name"
            />
            <div className="row" style={{ marginTop: 14 }}>
              <button className="btn btn-ghost" onClick={() => setDeleting(null)}>
                Cancel
              </button>
              <span className="spacer" />
              <button
                className="btn btn-danger"
                disabled={confirmName.trim().toLowerCase() !== deleting.name.toLowerCase()}
                onClick={() => void doDelete()}
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
